import {Component, EventEmitter, Input, OnChanges, Output, SimpleChange} from '@angular/core';

@Component({
    selector: 'app-password-strength-bar',
    styles: [`
    .strengthBar {
      display: inline;
      list-style: none;
      margin: 0 0 0 15px;
      padding: 0;
      vertical-align: 2px;
    }

    .strengthBar .point {
      background: #DDD;
      border-radius: 2px;
      display: inline-block;
      height: 5px;
      margin-right: 1px;
      width: 20px;
    }

    .strengthBar .point:last-child {
      margin: 0;
    }
    .pre {
      white-space: pre;
    }
    .placement {
        position: relative;
        top: -15px;
    }
  `],
    template: `
    <div class="placement" id="strength" #strength>
      <small class="mr-1">{{barLabel}}</small>
      <ul id="strengthBar" class="strengthBar">
        <li id="bar0" class="point" [style.background-color]="bar0"></li>
        <li class="point" [style.background-color]="bar1"></li>
        <li class="point" [style.background-color]="bar2"></li>
        <li class="point" [style.background-color]="bar3"></li>
        <li class="point" [style.background-color]="bar4"></li>
      </ul>
      <small [hidden]="!strengths" class="pre">  {{strengthLabel}}</small>
    </div>
  `
})
export class PasswordStrengthBarComponent implements OnChanges {
    @Input() passwordToCheck: string;
    @Input() barLabel: string;
    @Input() barColors: string[];
    @Input() baseColor: string;
    @Input() strengthLabels: string[];
    @Output() onStrengthChanged: EventEmitter<number> = new EventEmitter<number>();

    bar0: string;
    bar1: string;
    bar2: string;
    bar3: string;
    bar4: string;

    strengthLabel: string;

    private colors: string[];
    strengths: string[];
    private defaultColors = ['#F00', '#ff710d', '#ffc300', '#ffdb00', '#0F0'];
    private defaultBaseColor = '#DDD';

    constructor() {
        this.colors = this.defaultColors;
    }

    private checkBarColors(): void {
        // Accept custom colors if input is valid, otherwise the default colors will be used
        if (this.barColors && this.barColors.length === 5) {
            this.colors = this.barColors.slice();
        } else {
            this.colors = this.defaultColors;
        }

        this.strengths = this.strengthLabels && this.strengthLabels.length === 5 ? this.strengthLabels.slice() : null;
        this.setStrengthLabel(0);

        if (!/(^#[0-9A-F]{6}$)|(^#[0-9A-F]{3}$)/i.test(this.baseColor)) {
            this.baseColor = this.defaultBaseColor;
        }
    }

    private static measureStrength(pass: string) {
        let score = 0;

        // bonus points for mixing it up
        const variations = {
            digits: /\d/.test(pass),
            lower: /[a-z]/.test(pass),
            upper: /[A-Z]/.test(pass),
            special: /\W/.test(pass)
        };
        if(variations.digits){
            score += 20;
        }
        if(variations.lower){
            score += 20;
        }
        if(variations.upper){
            score += 20;
        }
        if(variations.special){
            score += 20;
        }
        if(pass.length >= 12){
            score += 20;
        }

        return score;
    }

    private getColor(score: number) {
        let idx = 0;
        if (score > 90) {
            idx = 4;
        } else if (score > 70) {
            idx = 3;
        } else if (score >= 40) {
            idx = 2;
        } else if (score >= 20) {
            idx = 1;
        }
        return {
            idx: idx + 1,
            col: this.colors[idx]
        };
    }

    getStrengthIndexAndColor(password: string) {
        return this.getColor(PasswordStrengthBarComponent.measureStrength(password));
    }

    ngOnChanges(changes: { [propName: string]: SimpleChange }): void {
        let password = '';
        if(changes && changes['passwordToCheck'] && changes['passwordToCheck'].currentValue){
            password = changes['passwordToCheck'].currentValue;
        }

        this.checkBarColors();
        this.setBarColors(5, this.baseColor);
        let strength = 0;
        if (password) {
            const c = this.getStrengthIndexAndColor(password);
            strength = c.idx - 1;
            this.setStrengthLabel(strength);
            this.setBarColors(c.idx, c.col);
        }
        this.onStrengthChanged.emit(strength);
    }

    private setBarColors(count: number, col: string) {
        for (let _n = 0; _n < count; _n++) {
            this['bar' + _n] = col;
        }
    }
    private setStrengthLabel(index: number) {
        if (this.strengths) {
            this.strengthLabel = this.strengths[index];
        }
    }
}
